import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import AppProviders from './providers/AppProviders';
import Layout from './components/layout/Layout';
import CheckoutLayout from './components/layout/CheckoutLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { runCleanupOnce } from './utils/cleanOldData';
import FloatingWhatsApp from './components/common/FloatingWhatsApp';
import CookieConsent from './components/common/CookieConsent';
import InstallPrompt from './components/common/InstallPrompt';
import CommandPalette from './components/common/CommandPalette';
import ShortcutsDialog from './components/common/ShortcutsDialog';
import ErrorBoundary from './components/common/ErrorBoundary';
import { usePageTracking } from './hooks/usePageTracking';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Eager-loaded components (critical path)
import Login from './pages/Login';
import OrganizationSignup from './pages/OrganizationSignup';
import AdminSetup from './pages/AdminSetup';

// Public/Landing pages (eager-loaded for SEO/performance)
import { LandingPage, FeaturesPage, PricingPage, AboutPage, ContactPage, ConfirmAppointment } from './pages/public';
import ExitIntentPopup from './components/landing/ExitIntentPopup';

// Blog pages (lazy-loaded for code splitting)
const Blog = lazy(() => import('./pages/public/Blog'));
const BlogPost = lazy(() => import('./pages/public/BlogPost'));

// Landing pages for paid ads
const LPPsicologoAutonomo = lazy(() => import('./pages/public/LPPsicologoAutonomo'));
const LPGoogle = lazy(() => import('./pages/public/LPGoogle'));
const LPInstagram = lazy(() => import('./pages/public/LPInstagram'));

// Trial signup success (public, fires Lead conversion event)
const TrialStartedSuccess = lazy(() => import('./pages/TrialStartedSuccess'));

// 404 page (lazy-loaded)
const NotFoundPage = lazy(() => import('./pages/public/NotFoundPage'));

// Public booking page (lazy-loaded)
const RequestBooking = lazy(() => import('./pages/public/RequestBooking'));

// Lazy-loaded pages (code splitting)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Patients = lazy(() => import('./pages/Patients'));
const PatientFormPage = lazy(() => import('./pages/PatientForm'));
const Sessions = lazy(() => import('./pages/Sessions'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const SubscriptionManagement = lazy(() => import('./pages/SubscriptionManagement'));
const SubscriptionCheckout = lazy(() => import('./pages/SubscriptionCheckout'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const OrganizationBranding = lazy(() => import('./pages/OrganizationBranding'));
const VideoConsultation = lazy(() => import('./pages/VideoConsultation'));
const PublicVideoCall = lazy(() => import('./pages/PublicVideoCall'));
const ReferralProgram = lazy(() => import('./pages/ReferralProgram'));
const UpgradePlan = lazy(() => import('./pages/UpgradePlan'));
const WaitingList = lazy(() => import('./pages/WaitingList'));
const DocumentGenerator = lazy(() => import('./pages/DocumentGenerator'));
const OnlineBookingSettings = lazy(() => import('./pages/OnlineBookingSettings'));
const TemplatesLibrary = lazy(() => import('./pages/TemplatesLibrary'));
const ProductivityReports = lazy(() => import('./pages/ProductivityReports'));
const InsuranceManagement = lazy(() => import('./pages/InsuranceManagement'));
const DataExport = lazy(() => import('./pages/DataExport'));

// Admin module lazy imports
const AdminProtectedRoute = lazy(() => import('./admin/components/AdminProtectedRoute'));
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const Organizations = lazy(() => import('./admin/pages/Organizations'));
const AllUsers = lazy(() => import('./admin/pages/AllUsers'));
const PricingConfig = lazy(() => import('./admin/pages/PricingConfig'));
const InviteKeys = lazy(() => import('./admin/pages/InviteKeys'));
const LeadsManagement = lazy(() => import('./admin/pages/LeadsManagement'));
const Analytics = lazy(() => import('./admin/pages/Analytics'));
const Reengagement = lazy(() => import('./admin/pages/Reengagement'));

// Patient Portal lazy imports
const PatientLogin = lazy(() => import('./pages/patient-portal/PatientLogin'));
const PatientPortalWrapper = lazy(() => import('./pages/patient-portal/PatientPortalWrapper'));
const PatientDashboard = lazy(() => import('./pages/patient-portal/PatientDashboard'));
const MyAppointments = lazy(() => import('./pages/patient-portal/MyAppointments'));
const MyDocuments = lazy(() => import('./pages/patient-portal/MyDocuments'));
const MyProfile = lazy(() => import('./pages/patient-portal/MyProfile'));

// Loading fallback component
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={60} />
    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
      Carregando...
    </Box>
  </Box>
);

// Rotas públicas onde o botão WhatsApp deve aparecer
const PUBLIC_ROUTES = ['/', '/funcionalidades', '/planos', '/sobre', '/contato', '/signup', '/signup/success', '/login', '/blog'];

// Wrapper para mostrar WhatsApp apenas em rotas públicas
const FloatingWhatsAppWrapper: React.FC = () => {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith('/confirm/') || location.pathname.startsWith('/blog') || location.pathname.startsWith('/lp/')
  );

  if (!isPublicRoute) return null;
  return <FloatingWhatsApp />;
};

// Global keyboard shortcuts and command palette manager
const KeyboardShortcutsManager: React.FC = () => {
  const {
    isCommandPaletteOpen,
    setCommandPaletteOpen,
    isShortcutsDialogOpen,
    setShortcutsDialogOpen,
  } = useKeyboardShortcuts();

  return (
    <>
      <CommandPalette
        open={isCommandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
      <ShortcutsDialog
        open={isShortcutsDialogOpen}
        onClose={() => setShortcutsDialogOpen(false)}
      />
    </>
  );
};

// Wrapper para tracking de páginas públicas (analytics interno)
const PublicPageTracker: React.FC = () => {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.some(route =>
    location.pathname === route ||
    location.pathname.startsWith('/confirm/') ||
    location.pathname.startsWith('/agendar/') ||
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/lp/')
  );

  // Hook que faz tracking automático quando a rota muda
  usePageTracking({ autoTrack: isPublicRoute });

  return null;
};

function App() {
  // Clean old localStorage data on first run (migration to new Supabase)
  useEffect(() => {
    const didCleanup = runCleanupOnce();
    if (didCleanup) {
      console.log('Old localStorage data cleaned for new Supabase migration');
    }
  }, []);

  return (
    <AppProviders>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <FloatingWhatsAppWrapper />
        <PublicPageTracker />
        <InstallPrompt />
        <CookieConsent />
        <KeyboardShortcutsManager />
        {/* ErrorBoundary global captura qualquer throw em render — antes
            qualquer exceção em componente filho gerava "tela em branco"
            (white screen of death) e usuário precisava fechar/reabrir app.
            Cliente novo reportou esse exato sintoma 2026-05-20. */}
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* Rotas públicas - Site Institucional */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/funcionalidades" element={<><FeaturesPage /><ExitIntentPopup delay={15000} /></>} />
            <Route path="/planos" element={<><PricingPage /><ExitIntentPopup delay={15000} /></>} />
            <Route path="/sobre" element={<><AboutPage /><ExitIntentPopup delay={15000} /></>} />
            <Route path="/contato" element={<><ContactPage /><ExitIntentPopup delay={15000} /></>} />

            {/* Blog */}
            <Route path="/blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
            <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPost /></Suspense>} />

            {/* Landing Pages para anúncios */}
            <Route path="/lp/psicologo-autonomo" element={<Suspense fallback={<PageLoader />}><LPPsicologoAutonomo /></Suspense>} />
            <Route path="/lp/google" element={<Suspense fallback={<PageLoader />}><LPGoogle /></Suspense>} />
            <Route path="/lp/instagram" element={<Suspense fallback={<PageLoader />}><LPInstagram /></Suspense>} />

            {/* Rota pública - Confirmação de Agendamento */}
            <Route path="/confirm/:token" element={<ConfirmAppointment />} />

            {/* Public booking page */}
            <Route path="/agendar/:slug" element={<Suspense fallback={<PageLoader />}><RequestBooking /></Suspense>} />

            {/* Public video call page - accessible without authentication */}
            <Route path="/video/:appointmentId" element={<Suspense fallback={<PageLoader />}><PublicVideoCall /></Suspense>} />

            {/* Rotas de autenticação */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<OrganizationSignup />} />
            <Route path="/signup/success" element={<Suspense fallback={<PageLoader />}><TrialStartedSuccess /></Suspense>} />

            {/* Rotas protegidas - Area clinica */}
            <Route element={<ProtectedRoute />}>
              {/* Video consultation route - full screen without layout */}
              <Route path="video-call/:appointmentId" element={<VideoConsultation />} />

              {/* Checkout routes - layout isolado SEM sidebar (usuário deve pagar) */}
              <Route element={<CheckoutLayout />}>
                <Route path="subscription/checkout" element={<SubscriptionCheckout />} />
                <Route path="subscription/success" element={<SubscriptionSuccess />} />
              </Route>

              {/* Main app routes - layout completo com sidebar */}
              <Route element={<Layout />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="patients" element={<Patients />} />
                <Route path="patients/new" element={<PatientFormPage />} />
                <Route path="patients/:id/edit" element={<PatientFormPage />} />
                <Route path="sessions" element={<Sessions />} />
                <Route path="sessions/:patientId" element={<Sessions />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="team" element={<TeamManagement />} />
                <Route path="settings" element={<OrganizationSettings />} />
                <Route path="roles" element={<RoleManagement />} />
                <Route path="subscription" element={<SubscriptionManagement />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="notifications" element={<NotificationSettings />} />
                <Route path="branding" element={<OrganizationBranding />} />
                <Route path="referrals" element={<ReferralProgram />} />
                <Route path="upgrade" element={<UpgradePlan />} />
                <Route path="waiting-list" element={<WaitingList />} />
                <Route path="documents" element={<DocumentGenerator />} />
                <Route path="templates" element={<TemplatesLibrary />} />
                <Route path="online-booking" element={<OnlineBookingSettings />} />
                <Route path="reports" element={<ProductivityReports />} />
                <Route path="insurance" element={<InsuranceManagement />} />
                <Route path="export" element={<DataExport />} />
              </Route>
            </Route>

            {/* Rotas protegidas - Admin do sistema */}
            <Route element={<AdminProtectedRoute />}>
              <Route path="admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="reengagement" element={<Reengagement />} />
                <Route path="leads" element={<LeadsManagement />} />
                <Route path="organizations" element={<Organizations />} />
                <Route path="users" element={<AllUsers />} />
                <Route path="invite-keys" element={<InviteKeys />} />
                <Route path="pricing" element={<PricingConfig />} />
              </Route>
              {/* /admin-setup expõe lista de users + cria admin. Antes era pública —
                  vazava PII e dava bait pra privilege escalation. Agora atrás da guarda. */}
              <Route path="admin-setup" element={<AdminSetup />} />
            </Route>

            {/* Portal do Paciente - autenticação separada */}
            <Route path="portal/login" element={<PatientLogin />} />
            <Route path="portal" element={<PatientPortalWrapper />}>
              <Route index element={<PatientDashboard />} />
              <Route path="agendamentos" element={<MyAppointments />} />
              <Route path="documentos" element={<MyDocuments />} />
              <Route path="perfil" element={<MyProfile />} />
            </Route>

            {/* Redirecionamento padrão */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AppProviders>
  );
}

export default App;
